import { Component } from '@theme/component';
import { SlideshowSelectEvent, ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

const STACKED_CAROUSEL_BREAKPOINT = 1024;
const STACKED_CAROUSEL_ACTIVE_OFFSET_RATIO = 0.35;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /**
   * Wires gallery listeners after the component is connected to the DOM.
   * Rebinds slideshow state handling every time the gallery is re-rendered.
   */
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });

    window.addEventListener('scroll', this.#handleViewportScroll, { signal, passive: true });
    window.addEventListener('resize', this.#handleViewportChange, { signal, passive: true });
    window.addEventListener('scrollend', this.#handleScrollEnd, { signal, passive: true });

    // Deferred to the next frame: right after `connectedCallback` runs,
    // descendant custom elements (e.g. the slideshow-component providing
    // `this.slideshow`) may not have connected/upgraded yet — most notably
    // when this element is swapped in via `replaceWith` on variant change.
    requestAnimationFrame(() => {
      this.#bindDotClickHandlers();
      this.#syncStackedCarouselState();
    });

    // On a page reload, the browser restores the previous scroll position -
    // sometimes after this initial sync already ran against scrollY 0,
    // leaving the first dot marked active while the page is actually
    // scrolled further down. Re-check once everything (including images,
    // which affect slide heights) has finished loading and any scroll
    // restoration has settled.
    if (document.readyState !== 'complete') {
      window.addEventListener('load', this.#handleViewportChange, { signal, once: true });
    }
  }

  #controller = new AbortController();

  /**
   * Index of the slide a dot click is currently scrolling towards, while that
   * scroll hasn't settled yet. Null when no programmatic scroll is pending.
   * @type {number | null}
   */
  #pendingScrollIndex = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #pendingScrollTimeout;

  /**
   * Cleans up all listeners registered through the component abort controller.
   */
  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);

    // The freshly rendered gallery always resets to slide/dot 0 (its markup
    // has no concept of "the slide the previous gallery was scrolled to",
    // and the new variant's media count may not even have a slide at the old
    // index). In stacked desktop mode that leaves the page scrolled wherever
    // it was - mismatched with the now-active first dot - so snap it back to
    // the top of the gallery to match.
    if (newMediaGallery instanceof MediaGallery && newMediaGallery.#isStackedCarouselMode()) {
      newMediaGallery.#scrollToSlide(0, 'instant');
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Keeps the active state in sync when the slideshow changes through native controls.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    this.#setCurrentSlide(event.detail.index);
  };

  #scrollTicking = false;

  /**
   * Throttles viewport scroll handling so the active dot follows the top visible slide.
   */
  #handleViewportScroll = () => {
    if (!this.#isStackedCarouselMode() || this.#scrollTicking) return;

    this.#scrollTicking = true;
    requestAnimationFrame(() => {
      this.#scrollTicking = false;
      this.#syncStackedCarouselState();
    });
  };

  /**
   * Re-evaluates stacked carousel state after viewport size changes.
   */
  #handleViewportChange = () => {
    this.#syncStackedCarouselState();
  };

  /**
   * Overrides dot clicks in stacked desktop mode so dots scroll the page
   * to the corresponding media block instead of relying on horizontal slideshow scroll.
   */
  #bindDotClickHandlers() {
    const dots = this.slideshow?.refs?.dots;
    if (!dots?.length) return;

    for (const [index, dot] of dots.entries()) {
      dot.addEventListener(
        'click',
        (event) => {
          if (!this.#isStackedCarouselMode()) return;

          event.preventDefault();
          this.#scrollToSlide(index);
        },
        { signal: this.#controller.signal }
      );
    }
  }

  /**
   * Detects which slide currently occupies the top of the viewport and marks it as active.
   * Falls back to the slide whose top edge is closest to the sticky header offset.
   */
  #syncStackedCarouselState() {
    if (!this.#isStackedCarouselMode()) return;
    // A dot click is still settling into place - trust the clicked slide
    // instead of recomputing from the (possibly clamped) scroll position.
    if (this.#pendingScrollIndex !== null) return;

    const activeIndex = this.#detectActiveStackedSlideIndex();
    if (activeIndex === null) return;

    this.#setCurrentSlide(activeIndex);
  }

  /**
   * Computes which slide currently occupies the top of the viewport, without
   * mutating any state. Falls back to the slide whose top edge is closest to
   * the sticky header offset.
   *
   * @returns {number | null} The detected slide index, or null if there are no slides.
   */
  #detectActiveStackedSlideIndex() {
    const slides = this.slideshow?.slides;
    if (!slides?.length) return null;

    // Before the gallery has scrolled past its own start, the first slide
    // hasn't reached the (now much lower) reference line yet if it's shorter
    // than the offset - keep it active instead of skipping straight to the
    // slide that happens to sit at that line.
    if (slides[0].getBoundingClientRect().top >= this.#getStickyHeaderOffset()) {
      return 0;
    }

    const viewportTop = this.#getViewportTopOffset();
    const activeIndex = slides.findIndex((slide) => {
      const { top, bottom } = slide.getBoundingClientRect();
      return top <= viewportTop && bottom > viewportTop;
    });

    if (activeIndex !== -1) return activeIndex;

    return slides.reduce((closestIndex, slide, index) => {
      const closestSlide = slides[closestIndex];
      const closestDistance = Math.abs(closestSlide.getBoundingClientRect().top - viewportTop);
      const currentDistance = Math.abs(slide.getBoundingClientRect().top - viewportTop);

      return currentDistance < closestDistance ? index : closestIndex;
    }, 0);
  }

  /**
   * Updates the slideshow current index so built-in dots/counter state stays in sync.
   *
   * @param {number} index - The slide index that should become active.
   */
  #setCurrentSlide(index) {
    if (!this.slideshow) return;
    if (this.slideshow.current === index) return;

    this.slideshow.current = index;
  }

  /**
   * Scrolls the document to the requested slide in stacked desktop mode.
   *
   * @param {number} index - The target slide index.
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #scrollToSlide(index, behavior = 'smooth') {
    const slide = this.slideshow?.slides?.[index];
    if (!slide) return;

    this.#setCurrentSlide(index);
    this.#pendingScrollIndex = index;

    clearTimeout(this.#pendingScrollTimeout);
    // Fallback for browsers without the 'scrollend' event, or when the
    // scroll is clamped short (e.g. not enough room above to reach the
    // target offset) and never actually settles at a new position.
    this.#pendingScrollTimeout = setTimeout(this.#handleScrollEnd, 800);

    const top = window.scrollY + slide.getBoundingClientRect().top - this.#getViewportTopOffset();
    window.scrollTo({
      top: Math.max(top, 0),
      behavior,
    });
  }

  /**
   * Clears the pending programmatic-scroll guard once a dot-click scroll has
   * settled. Deliberately does NOT re-run the scroll-based detection here:
   * when the target slide sits close enough to the top that the scroll gets
   * clamped short (not enough room above to reach the usual offset), re-
   * detecting from the resulting scroll position would incorrectly fall back
   * to the first slide. The clicked slide is trusted as-is; normal detection
   * resumes on the next real user scroll.
   */
  #handleScrollEnd = () => {
    clearTimeout(this.#pendingScrollTimeout);
    this.#pendingScrollIndex = null;
  };

  /**
   * Returns the viewport reference line used to determine the active slide.
   * Sits partway down the visible area (below the sticky header) rather than
   * right at its edge, so a slide only becomes active once it's already
   * meaningfully in view - not just barely peeking out from under the header.
   *
   * @returns {number} Viewport offset adjusted for the sticky header.
   */
  #getViewportTopOffset() {
    const headerOffset = this.#getStickyHeaderOffset();
    const visibleHeight = window.innerHeight - headerOffset;

    return headerOffset + visibleHeight * STACKED_CAROUSEL_ACTIVE_OFFSET_RATIO;
  }

  /**
   * Reads the sticky header offset from theme CSS variables.
   *
   * @returns {number} Sticky header height in pixels.
   */
  #getStickyHeaderOffset() {
    const rootStyles = getComputedStyle(document.documentElement);
    const stickyOffset = parseFloat(rootStyles.getPropertyValue('--sticky-header-offset'));

    return Number.isFinite(stickyOffset) ? stickyOffset : 0;
  }

  /**
   * Checks whether the gallery is currently rendered as a stacked vertical carousel.
   *
   * @returns {boolean} True when the desktop stacked carousel layout is active.
   */
  #isStackedCarouselMode() {
    if (this.presentation !== 'carousel') return false;
    if (window.innerWidth < STACKED_CAROUSEL_BREAKPOINT) return false;

    const slidesContainer = this.slideshow?.refs.scroller;
    if (!(slidesContainer instanceof HTMLElement)) return false;

    return getComputedStyle(slidesContainer).flexDirection === 'column';
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  /**
   * Returns the slideshow instance rendered inside the gallery.
   *
   * @returns {import('./slideshow').Slideshow | undefined}
   */
  get slideshow() {
    return this.refs.slideshow;
  }

  /**
   * Returns gallery media elements registered via refs.
   *
   * @returns {HTMLElement[] | undefined}
   */
  get media() {
    return this.refs.media;
  }

  /**
   * Returns the configured gallery presentation mode.
   *
   * @returns {string | undefined}
   */
  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
